import React from 'react';
import styled from 'styled-components';

import { Typography } from '@mui/material';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';

import { PALETTE } from '~/assets/styles/base';

const Wrapper = styled.div``;

type StyledAlertProps = {
  $width?: string;
  $position?: string;
};

const StyledAlert = styled.div<StyledAlertProps>`
  border: 1px solid ${PALETTE.error.main};
  background-color: ${PALETTE.error.light};
  color: ${PALETTE.error.main};
  padding: 8px 12px;
  margin-top: 15px;
  width: ${({ $width }) => $width ?? 'fit-content'};
  ${({ $position }) =>
    $position === 'center' &&
    `
    position: relative;
    left: 50%;
    transform: translateX(-50%);
  `}
  ${({ $position }) =>
    $position === 'centerAbsolute' &&
    `
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
 `}
  ${({ $position }) =>
    $position === 'right' &&
    `
    position: relative;
    left: 100%;
    transform: translateX(-100%);
  `}
  ${({ $position }) =>
    $position === 'left' &&
    `
    position: relative;
  `}
  ${({ $position }) =>
    $position === 'rightAbsolute' &&
    `
    position: absolute;
    left: 101%;
    transform: translateX(-101%);
 `}
  ${({ $position }) =>
    $position === 'leftAbsolute' &&
    `
    position: absolute;
    left: 6px;
    transform: translateX(6px);
 `}

  // 吹き出し
  :before,
  :after {
    content: '';
    border: solid transparent;
    pointer-events: none;
    position: absolute;
    left: 50%;
  }
  :before {
    border: 7px solid transparent;
    border-bottom: 13px solid ${PALETTE.error.main};
    margin-left: -7px;
    margin-bottom: 0;
    bottom: 100%;
  }
  :after {
    border: 6px solid transparent;
    border-bottom: 11px solid ${PALETTE.error.light};
    margin-left: -6px;
    bottom: 97%;
  }
  // 上下自動切替え 対象の上部に配置の時
  .MuiTooltip-popper[data-popper-placement='top'] & {
    bottom: 0;
    :before {
      transform: scale(1, -1);
      bottom: -20px;
    }
    :after {
      transform: scale(1, -1);
      bottom: -17px;
    }
  }
`;

const StyledTypography = styled(Typography)`
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  line-height: 1.3;
  svg {
    font-size: 18px;
    margin-right: 2px;
  }
  a {
    color: ${PALETTE.error.main};
  }
`;

type Props = {
  message: string;
  width?: string;
  position?: 'center' | 'centerAbsolute' | 'left' | 'right' | 'rightAbsolute' | 'leftAbsolute';
  dataTestId?: string;
};

const InputValidationErrorBox: React.FC<Props> = ({
  message,
  dataTestId,
  width, // 親から横幅を調整
  position, // 親から表示位置を調整
}): JSX.Element => (
  <Wrapper data-testid="uiParts-InputValidationErrorBox">
    <StyledAlert
      color="error"
      $width={width}
      $position={position}
      /* istanbul ignore next */
      data-testid={
        dataTestId
          ? `uiParts-InputValidationErrorBox-alert-${dataTestId}`
          : 'uiParts-InputValidationErrorBox-alert'
      }
    >
      <StyledTypography variant="body3">
        <WarningRoundedIcon
          /* istanbul ignore next */
          data-testid={
            dataTestId
              ? `uiParts-InputValidationErrorBox-icon-${dataTestId}`
              : 'uiParts-InputValidationErrorBox-icon'
          }
        />
        <span
          /* istanbul ignore next */
          data-testid={
            dataTestId
              ? `uiParts-InputValidationErrorBox-errorMessage-${dataTestId}`
              : 'uiParts-InputValidationErrorBox-errorMessage'
          }
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: message,
          }}
        />
      </StyledTypography>
    </StyledAlert>
  </Wrapper>
);

export default InputValidationErrorBox;
